import { FormEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Check,
  Search as SearchIcon,
  X,
  Trash2,
  ZoomIn,
  ZoomOut,
  ChevronDown,
  ChevronUp,
  Loader2,
  SlidersHorizontal,
} from "lucide-react";
import { api, Camera, SearchBody, SearchMode, SpatialFilter, TrackHit } from "../api";
import { ClipScrubber } from "../components/ClipScrubber";
import { ClipPlayerCanvas, useDirectReplay } from "../useDirectReplay";
import { InfoHint } from "../components/InfoHint";
import { useBackdropDismiss } from "../components/Modal";
import { PageHeader } from "../components/PageHeader";
import { useScope } from "../components/ScopeContext";
import { SearchableSelect, type SearchableOption } from "../components/SearchableSelect";
import { SpatialFilterPanel } from "../components/SpatialFilterPanel";
import { eventInside, onEscapeKey } from "../outsideDismiss";
import { buildDescriptionRows, captionPromptFromConfig, trackActivityLine, trackCardMainText, trackCaptionProse, matchesCaptionFilters } from "../captionDescription";
import { useResultsColumns } from "../hooks/useResultsColumns";
import {
  AmPm,
  DateTimeParts,
  emptyDateTimeParts,
  hasDateTimeParts,
  HOURS_12,
  MINUTES,
  matchesTimeOfDayWindow,
  partsToIso,
  readDateTimeParts,
  writeDateTimeParts,
} from "../searchDateTime";

type FilterState = {
  fromDate: string;
  fromHour: string;
  fromMinute: string;
  fromAmPm: AmPm;
  toDate: string;
  toHour: string;
  toMinute: string;
  toAmPm: AmPm;
  cameras: string[];
  sex: string;
  age: string;
  skin: string;
  hair: string;
  sort: "relevance" | "recency";
  /** Drop hits below this match, 0–1. Sent as `min_score`. */
  minScore: number;
  /** Region drawn on one camera's frame; null when no area is selected. */
  spatial: SpatialFilter | null;
};

/** Off by default: a relevance floor hides results without saying so, which is
 *  the wrong default for a tool people use to prove something was NOT there.
 *  Typed rather than dragged — it is a number the operator compares against
 *  the score printed on every result, not a feel. */
const DEFAULT_MIN_SCORE = 0;

const PLACEHOLDER_ACTIVITY = "women placing the keys on the table";
const PLACEHOLDER_APPEARANCE =
  "man, adult, dark skin, black hair, black jacket, black pants, black shoes";

const SEARCH_PLACEHOLDERS: Record<SearchMode, readonly string[]> = {
  both: [PLACEHOLDER_ACTIVITY, PLACEHOLDER_APPEARANCE],
  appearance: [PLACEHOLDER_APPEARANCE],
  activity: [PLACEHOLDER_ACTIVITY],
};

const PLACEHOLDER_ROTATE_MS = 4000;

function readInitialQuery(): string {
  return new URLSearchParams(window.location.search).get("q") ?? "";
}

function readSearchMode(): SearchMode {
  const mode = new URLSearchParams(window.location.search).get("mode");
  return mode === "appearance" || mode === "activity" ? mode : "both";
}

type ExclusiveByMode = { appearance: boolean; activity: boolean };

function exclusiveOn(mode: SearchMode, byMode: ExclusiveByMode): boolean {
  return mode === "appearance" || mode === "activity" ? byMode[mode] : false;
}

function readExclusiveByMode(): ExclusiveByMode {
  const p = new URLSearchParams(window.location.search);
  const mode = p.get("mode");
  const on = p.get("exclusive") === "1";
  return {
    appearance: mode === "appearance" && on,
    activity: mode === "activity" && on,
  };
}

function readUrlFilters(): FilterState {
  const p = new URLSearchParams(window.location.search);
  const cams = p.get("cameras");
  const from = readDateTimeParts(p, "from", "from");
  const to = readDateTimeParts(p, "to", "to");
  return {
    fromDate: from.date,
    fromHour: from.hour,
    fromMinute: from.minute,
    fromAmPm: from.ampm,
    toDate: to.date,
    toHour: to.hour,
    toMinute: to.minute,
    toAmPm: to.ampm,
    cameras: cams ? cams.split(",").filter(Boolean) : [],
    sex: p.get("sex") ?? "",
    age: p.get("age") ?? "",
    skin: p.get("skin") ?? "",
    hair: p.get("hair") ?? "",
    sort: p.get("sort") === "recency" ? "recency" : "relevance",
    minScore: readMinScore(p),
    spatial: readSpatial(p),
  };
}

function readMinScore(p: URLSearchParams): number {
  const n = Number(p.get("min_score"));
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : DEFAULT_MIN_SCORE;
}

// The region rides in the URL as "cam:x1,y1,x2,y2:overlap" so a spatial search
// can be shared or reloaded like any other filter.
function readSpatial(p: URLSearchParams): SpatialFilter | null {
  const raw = p.get("area");
  if (!raw) return null;
  const [cameraId, boxStr, overlapStr] = raw.split(":");
  const nums = (boxStr ?? "").split(",").map(Number);
  if (!cameraId || nums.length !== 4 || nums.some((n) => !Number.isFinite(n))) {
    return null;
  }
  const overlap = Number(overlapStr);
  return {
    camera_id: cameraId,
    bbox: [nums[0], nums[1], nums[2], nums[3]],
    min_overlap: Number.isFinite(overlap) ? overlap : 0.5,
    at_ts: p.get("area_at") ?? undefined,
  };
}

/** Value equality, so the panel re-emitting an unchanged region is a no-op
 *  instead of a state update that restarts the search. */
function sameSpatial(a: SpatialFilter | null, b: SpatialFilter | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.camera_id === b.camera_id &&
    a.min_overlap === b.min_overlap &&
    a.bbox.every((v, i) => v === b.bbox[i])
  );
}

function writeSpatial(p: URLSearchParams, s: SpatialFilter | null): void {
  if (!s) return;
  const box = s.bbox.map((v) => v.toFixed(4)).join(",");
  p.set("area", `${s.camera_id}:${box}:${s.min_overlap}`);
  if (s.at_ts) p.set("area_at", s.at_ts);
}

function writeUrlFilters(
  f: FilterState,
  query: string,
  mode: SearchMode,
  exclusive = false,
): void {
  const p = new URLSearchParams();
  if (query.trim()) p.set("q", query.trim());
  if (mode === "appearance" || mode === "activity") p.set("mode", mode);
  if (exclusive && (mode === "appearance" || mode === "activity")) {
    p.set("exclusive", "1");
  }
  writeDateTimeParts(p, "from", {
    date: f.fromDate,
    hour: f.fromHour,
    minute: f.fromMinute,
    ampm: f.fromAmPm,
  });
  writeDateTimeParts(p, "to", {
    date: f.toDate,
    hour: f.toHour,
    minute: f.toMinute,
    ampm: f.toAmPm,
  });
  if (f.cameras.length) p.set("cameras", f.cameras.join(","));
  if (f.sex) p.set("sex", f.sex);
  if (f.age) p.set("age", f.age);
  if (f.skin) p.set("skin", f.skin);
  if (f.hair) p.set("hair", f.hair);
  if (f.sort === "recency") p.set("sort", "recency");
  if (f.minScore !== DEFAULT_MIN_SCORE) p.set("min_score", String(f.minScore));
  writeSpatial(p, f.spatial);
  const qs = p.toString();
  const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState(null, "", next);
}

const AGES = ["child", "teenager", "adult", "senior"];
const SKINS = ["light", "medium", "dark"];
const HAIRS = ["black", "brown", "blonde", "red", "pink", "gray", "white"];

const FILTER_LABEL = "mb-1 w-full text-center text-sm font-medium text-bf-text";
const FILTER_CONTROL = "bf-input text-xs text-bf-muted";

const SEARCH_MODE_ITEMS: { mode: SearchMode; label: string }[] = [
  { mode: "both", label: "Anything" },
  { mode: "appearance", label: "Appearance" },
  { mode: "activity", label: "Activity" },
];

const SEARCH_BUTTON_LABEL: Record<SearchMode, string> = {
  both: "Search anything",
  appearance: "Search appearance",
  activity: "Search activity",
};

/** Search submit plus a chevron menu for Anything, Appearance, or Activity. */
function SearchSplitButton({
  pending,
  mode,
  exclusive,
  onSelectMode,
  onToggleExclusive,
}: {
  pending: boolean;
  mode: SearchMode;
  exclusive: ExclusiveByMode;
  onSelectMode: (next: SearchMode) => void;
  onToggleExclusive: (mode: "appearance" | "activity") => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let downOutside = false;
    const onDown = (e: PointerEvent) => {
      downOutside = !eventInside(e, ref.current);
    };
    const onUp = (e: PointerEvent) => {
      if (downOutside && !eventInside(e, ref.current)) setOpen(false);
    };
    const onKey = onEscapeKey(() => setOpen(false));
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex shrink-0">
      <div className="inline-flex overflow-hidden rounded-lg shadow-brand">
        <button
          className="bf-button rounded-none shadow-none"
          type="submit"
          disabled={pending}
        >
          {pending ? "Searching..." : SEARCH_BUTTON_LABEL[mode]}
        </button>
        <button
          type="button"
          disabled={pending}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Search mode"
          onClick={() => setOpen((v) => !v)}
          className="bf-button rounded-none border-l border-white/25 px-2 shadow-none"
        >
          <ChevronDown
            size={16}
            aria-hidden
            className={"transition " + (open ? "rotate-180" : "")}
          />
        </button>
      </div>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 min-w-[13.5rem] overflow-hidden rounded-lg border border-white/10 bg-bf-surface p-1 shadow-xl shadow-black/50"
        >
          {SEARCH_MODE_ITEMS.map((item) => {
            const on = mode === item.mode;
            const exclusiveChecked =
              item.mode !== "both" && exclusiveOn(item.mode, exclusive);
            return (
              <div
                key={item.mode}
                className={
                  "flex items-center gap-1 rounded-md " +
                  (on ? "bg-bf-violet/25" : "")
                }
              >
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={on}
                  onClick={() => {
                    onSelectMode(item.mode);
                    setOpen(false);
                  }}
                  className={
                    "flex min-w-0 flex-1 items-center justify-between gap-1.5 px-2 py-1.5 text-left text-sm transition " +
                    (on ? "text-white" : "text-bf-text hover:bg-white/5")
                  }
                >
                  <span>{item.label}</span>
                  {on && <Check size={14} className="shrink-0 text-bf-violet" />}
                </button>
                {item.mode !== "both" && (
                  <label
                    className={
                      "mr-0.5 flex shrink-0 items-center gap-1 rounded-md px-1 py-1 text-xs " +
                      (on
                        ? "cursor-pointer text-bf-muted hover:bg-white/5 hover:text-bf-text"
                        : "cursor-not-allowed text-bf-muted/40")
                    }
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-bf-violet disabled:opacity-40"
                      checked={on && exclusiveChecked}
                      disabled={!on}
                      onChange={() =>
                        onToggleExclusive(item.mode as "appearance" | "activity")
                      }
                    />
                    Exclusive
                  </label>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** A labelled dropdown in the filter row.
 *
 *  Uses `SearchableSelect` rather than a bare `<select>` so every dropdown on
 *  the page looks the same: a native select renders with the browser's own
 *  chrome, which reads as a different control sitting next to the themed ones.
 */
function FilterSelect({
  label,
  value,
  onChange,
  options,
  className = "w-32",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: SearchableOption[];
  className?: string;
}) {
  return (
    <div className="flex flex-col items-center">
      <span className={FILTER_LABEL}>{label}</span>
      <SearchableSelect
        className={className}
        ariaLabel={label}
        value={value}
        onChange={onChange}
        searchThreshold={99}
        options={options}
      />
    </div>
  );
}

const anyOption = (values: string[]): SearchableOption[] => [
  { value: "", label: "any" },
  ...values.map((v) => ({ value: v, label: v })),
];

function DateTimeBoundRow({
  label,
  date,
  hour,
  minute,
  ampm,
  onDate,
  onTime,
}: {
  label: string;
  date: string;
  hour: string;
  minute: string;
  ampm: AmPm;
  onDate: (v: string) => void;
  onTime: (next: Pick<DateTimeParts, "hour" | "minute" | "ampm">) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-2 min-w-0">
      <span className={`${FILTER_LABEL} self-end pb-2 shrink-0 w-9 text-center`}>{label}</span>
      <label className="flex flex-col items-center">
        <span className={FILTER_LABEL}>Date</span>
        <input
          type="date"
          className={FILTER_CONTROL}
          value={date}
          onChange={(e) => onDate(e.target.value)}
        />
      </label>
      <label className="flex flex-col items-center">
        <span className={FILTER_LABEL}>Hour</span>
        <select
          className={FILTER_CONTROL + " min-w-[4.5rem]"}
          value={hour}
          onChange={(e) => {
            const h = e.target.value;
            if (!h) {
              onTime({ hour: "", minute: "", ampm: "" });
              return;
            }
            onTime({
              hour: h,
              minute: minute || "00",
              ampm: ampm || "AM",
            });
          }}
        >
          <option value="">—</option>
          {HOURS_12.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col items-center">
        <span className={FILTER_LABEL}>Min</span>
        <select
          className={FILTER_CONTROL + " min-w-[4.5rem]"}
          value={minute}
          onChange={(e) => onTime({ hour, minute: e.target.value, ampm })}
          disabled={!hour}
        >
          <option value="">—</option>
          {MINUTES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col items-center">
        <span className={FILTER_LABEL}>AM/PM</span>
        <select
          className={FILTER_CONTROL + " min-w-[4.5rem]"}
          value={ampm}
          onChange={(e) => onTime({ hour, minute, ampm: e.target.value as AmPm })}
          disabled={!hour}
        >
          <option value="">—</option>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </label>
    </div>
  );
}

type ObjectFitMode = "cover" | "contain";

function isNormalizedBbox(bbox: number[]): boolean {
  return bbox.length >= 4 && bbox.every((v) => v >= 0 && v <= 1);
}

function bboxOverlayRect(
  bbox: number[],
  nw: number,
  nh: number,
  cw: number,
  ch: number,
  fit: ObjectFitMode,
) {
  if (bbox.length < 4 || nw <= 0 || nh <= 0 || cw <= 0 || ch <= 0) return null;

  let [x1, y1, x2, y2] = bbox;
  if (isNormalizedBbox(bbox)) {
    x1 *= nw;
    y1 *= nh;
    x2 *= nw;
    y2 *= nh;
  }

  const scale = fit === "cover" ? Math.max(cw / nw, ch / nh) : Math.min(cw / nw, ch / nh);
  const dw = nw * scale;
  const dh = nh * scale;
  const ox = (cw - dw) / 2;
  const oy = (ch - dh) / 2;

  return {
    left: ox + x1 * scale,
    top: oy + y1 * scale,
    width: Math.max(0, (x2 - x1) * scale),
    height: Math.max(0, (y2 - y1) * scale),
  };
}

function useFrameLayout(
  containerRef: React.RefObject<HTMLDivElement | null>,
  loaded: boolean,
) {
  const [layout, setLayout] = useState({ nw: 0, nh: 0, cw: 0, ch: 0 });

  useLayoutEffect(() => {
    const box = containerRef.current;
    const img = box?.querySelector("img");
    if (!box || !img || !loaded) return;

    const update = () => {
      setLayout({
        nw: img.naturalWidth,
        nh: img.naturalHeight,
        cw: box.clientWidth,
        ch: box.clientHeight,
      });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(box);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [containerRef, loaded]);

  return layout;
}

function TrackFrameImage({
  src,
  alt,
  className,
  onLoad,
}: {
  src: string;
  alt: string;
  className: string;
  onLoad?: () => void;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (failed) {
    return (
      <div
        className={
          className +
          " grid place-items-center bg-black/40 text-center text-xs text-bf-muted px-3"
        }
      >
        Frame unavailable
        <span className="mt-1 block text-[10px] opacity-70">
          Snapshot missing from storage (often after a data purge)
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onLoad={onLoad}
      onError={() => setFailed(true)}
      draggable={false}
    />
  );
}

function TrackFrameWithBbox({
  src,
  alt,
  bbox,
  fit,
  className,
  imgClassName,
}: {
  src: string;
  alt: string;
  bbox: number[];
  fit: ObjectFitMode;
  className?: string;
  imgClassName: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => setLoaded(false), [src]);

  const { nw, nh, cw, ch } = useFrameLayout(containerRef, loaded);
  const overlay = bboxOverlayRect(bbox, nw, nh, cw, ch, fit);

  return (
    <div ref={containerRef} className={"relative overflow-hidden " + (className ?? "")}>
      <TrackFrameImage
        src={src}
        alt={alt}
        className={imgClassName}
        onLoad={() => setLoaded(true)}
      />
      {overlay && (
        <div
          className="pointer-events-none absolute border-2 border-bf-violet rounded-sm"
          style={overlay}
        />
      )}
    </div>
  );
}

function ZoomableTrackFrame({
  src,
  alt,
  bbox,
}: {
  src: string;
  alt: string;
  bbox: number[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  useEffect(() => {
    setLoaded(false);
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, [src]);

  const { nw, nh, cw, ch } = useFrameLayout(containerRef, loaded);
  const overlay = bboxOverlayRect(bbox, nw, nh, cw, ch, "contain");

  const clampScale = (v: number) => Math.min(5, Math.max(1, v));

  function zoomBy(delta: number) {
    setScale((s) => {
      const next = clampScale(s + delta);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }

  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? 0.15 : -0.15);
  }

  function onPointerDown(e: React.PointerEvent) {
    if (scale <= 1) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.panX + (e.clientX - dragRef.current.x),
      y: dragRef.current.panY + (e.clientY - dragRef.current.y),
    });
  }

  function onPointerUp() {
    dragRef.current = null;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex items-center justify-between gap-2 text-xs text-bf-muted">
        <span>Scroll or use +/- to zoom · drag to pan when zoomed in</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => zoomBy(-0.25)}
            className="rounded p-1.5 text-bf-muted hover:bg-white/10 hover:text-bf-text"
            aria-label="Zoom out"
          >
            <ZoomOut size={16} />
          </button>
          <span className="min-w-[2.5rem] text-center">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            onClick={() => zoomBy(0.25)}
            className="rounded p-1.5 text-bf-muted hover:bg-white/10 hover:text-bf-text"
            aria-label="Zoom in"
          >
            <ZoomIn size={16} />
          </button>
        </div>
      </div>
      <div
        ref={containerRef}
        className="relative min-h-[40vh] flex-1 overflow-hidden rounded-md bg-black/30"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => zoomBy(scale < 2 ? 1 : -1)}
      >
        <div
          className="absolute inset-0 origin-center transition-transform duration-100"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})` }}
        >
          <div className="relative h-full w-full">
            <TrackFrameImage
              src={src}
              alt={alt}
              className="h-full w-full object-contain"
              onLoad={() => setLoaded(true)}
            />
            {overlay && (
              <div
                className="pointer-events-none absolute border-2 border-bf-violet rounded-sm"
                style={overlay}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function humanize(value: string): string {
  return value.replace(/_/g, " ");
}

function matchingScorePct(track: TrackHit): number {
  return track.matching_score ?? Math.round(track.score ?? 0);
}

function formatMatchingScore(track: TrackHit): string {
  return `Matching score ${matchingScorePct(track)}%`;
}

function tileActivityChips(hit: TrackHit, prompt: string): string[] {
  const line = trackActivityLine(hit, prompt);
  const extras = hit.activities_distinct.filter(
    (a) => a.trim().toLowerCase() !== line.toLowerCase(),
  );
  if (!trackCaptionProse(hit, prompt)) {
    return extras.slice(0, 3);
  }
  return (line ? [line, ...extras] : extras).slice(0, 3);
}

function TrackDetailPanel({ track, prompt }: { track: TrackHit; prompt: string }) {
  const activityLine = useMemo(
    () => trackActivityLine(track, prompt),
    [track, prompt],
  );
  const descriptionRows = useMemo(() => {
    if (!trackCaptionProse(track, prompt)) {
      return activityLine ? [{ label: "Activity", answer: activityLine }] : [];
    }
    const rows = buildDescriptionRows(prompt, track.summary);
    if (!activityLine) return rows;
    const idx = rows.findIndex((r) => r.label.toLowerCase() === "activity");
    if (idx >= 0) {
      const current = rows[idx].answer?.trim();
      if (!current || current === "—") {
        return rows.map((r, i) => (i === idx ? { ...r, answer: activityLine } : r));
      }
      return rows;
    }
    const last = rows[rows.length - 1];
    if (
      last &&
      last.answer.trim().toLowerCase() === activityLine.toLowerCase()
    ) {
      return [...rows.slice(0, -1), { label: "Activity", answer: last.answer }];
    }
    return [...rows, { label: "Activity", answer: activityLine }];
  }, [activityLine, prompt, track]);
  const extraActivities = track.activities_distinct.filter(
    (a) => a.trim().toLowerCase() !== activityLine.toLowerCase(),
  );
  const appearanceProse = useMemo(
    () => trackCaptionProse(track, prompt),
    [track, prompt],
  );
  const lead = appearanceProse || activityLine;

  return (
    <div className="space-y-4 overflow-y-auto pr-2" style={{ maxHeight: "calc(80vh - 100px)" }}>
      <div>
        <div className="font-semibold text-lg text-bf-text leading-tight">
          Track {track.track_id}
        </div>
        <div className="mt-1 font-mono text-[10px] text-bf-muted break-all">{track.track_key}</div>
        <div className="text-xs text-bf-muted mt-1">
          Camera {track.camera_id} · {track.num_observations} observation
          {track.num_observations === 1 ? "" : "s"} ·{" "}
          <span title="Best of semantic (dense) and keyword (sparse) vector similarity">
            {formatMatchingScore(track)}
          </span>
        </div>
        <div className="text-xs text-bf-muted mt-1 space-y-0.5">
          <div>
            First seen: {new Date(track.first_seen_utc).toLocaleString()}
          </div>
          <div>
            Last seen: {new Date(track.last_seen_utc).toLocaleString()}
          </div>
        </div>
      </div>

      {lead && (
        <p className="text-sm text-bf-text border-t border-white/10 pt-3">{lead}</p>
      )}

      {descriptionRows.length > 0 && (
        <div className="border-t border-white/10 pt-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-bf-muted mb-2">
            Details
          </div>
          <ul className="space-y-2">
            {descriptionRows.map(({ label, answer }) => (
              <li key={label} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-bf-muted shrink-0">{label}:</span>
                <span className="text-right text-bf-text">{humanize(answer)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {extraActivities.length > 0 && (
        <div className="border-t border-white/10 pt-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-bf-muted mb-2">
            Activities
          </div>
          <div className="flex flex-wrap gap-1">
            {extraActivities.map((a) => (
              <span
                key={a}
                className="rounded-full bg-bf-violet/20 px-2 py-0.5 text-xs text-bf-text"
              >
                {humanize(a)}
              </span>
            ))}
          </div>
        </div>
      )}

      {track.activity_history.length > 0 && (
        <div className="border-t border-white/10 pt-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-bf-muted mb-2">
            Activity history
          </div>
          <ul className="space-y-1 text-xs text-bf-muted">
            {track.activity_history.map((e, i) => (
              <li key={`${e.ts_ms}-${i}`}>
                {new Date(e.ts_ms).toLocaleString()} — {humanize(e.activity)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const MIN_TILE_WIDTH = 180;
const GRID_GAP_PX = 8;
const MIN_INITIAL_PER_CAMERA = 4;
const INITIAL_FETCH = 100;
const FETCH_STEP = 50;
const MAX_FETCH = 500;
const ALL_VIEW_ROW_BATCH = 2;

const SHOW_MORE_CLASS =
  "text-sm font-medium text-bf-magenta/90 transition hover:text-bf-magenta";

function DynamicResultsGrid({
  columns,
  gridRef,
  className,
  children,
}: {
  columns: number;
  gridRef?: RefObject<HTMLDivElement>;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      ref={gridRef}
      className={"grid gap-2 " + (className ?? "")}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {children}
    </div>
  );
}

function ResultHitTile({
  hit,
  vllmPrompt,
  sort,
  onSelect,
  className,
  compact = false,
}: {
  hit: TrackHit;
  vllmPrompt: string;
  sort: FilterState["sort"];
  onSelect: (h: TrackHit) => void;
  className?: string;
  compact?: boolean;
}) {
  const activityChips = tileActivityChips(hit, vllmPrompt);
  return (
    <button
      type="button"
      onClick={() => onSelect(hit)}
      className={
        "bf-card group w-full text-left transition hover:border-bf-violet/60 " +
        (compact ? "p-2 " : "") +
        (className ?? "")
      }
    >
      <div className="relative">
        <TrackFrameWithBbox
          src={hit.frame_url}
          alt={hit.track_key}
          bbox={hit.bbox}
          fit="cover"
          className="aspect-video w-full rounded-md"
          imgClassName="h-full w-full rounded-md object-cover"
        />
      </div>
      <div
        className={
          "flex items-center justify-between " +
          (compact ? "mt-1.5 text-xs text-bf-muted" : "mt-2 text-xs text-bf-muted")
        }
      >
        <span>{hit.camera_id}</span>
        <span className="flex items-center gap-1.5">
          {sort === "relevance" && (
            <span
              className="rounded-full bg-bf-violet/20 px-1.5 py-0.5 text-xs font-medium text-bf-text"
              title="Matching score"
            >
              {matchingScorePct(hit)}%
            </span>
          )}
          <span className={compact ? "truncate max-w-[6rem]" : ""}>
            {new Date(hit.last_seen_utc).toLocaleString()}
          </span>
        </span>
      </div>
      <div
        className={
          (compact ? "mt-1 text-sm font-medium line-clamp-2" : "mt-1 text-sm line-clamp-2") +
          " text-bf-text"
        }
      >
        {trackCardMainText(hit, vllmPrompt)}
      </div>
      {activityChips.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {activityChips.map((a) => (
            <span
              key={a}
              className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-bf-muted"
            >
              {a}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

function CameraResultsRow({
  cameraLabel,
  hits,
  visibleCount,
  rowColumns,
  canShowMore,
  canShowLess,
  onShowMore,
  onShowLess,
  vllmPrompt,
  sort,
  onSelect,
}: {
  cameraLabel: string;
  hits: TrackHit[];
  visibleCount: number;
  rowColumns: number;
  canShowMore: boolean;
  canShowLess: boolean;
  onShowMore: () => void;
  onShowLess: () => void;
  vllmPrompt: string;
  sort: FilterState["sort"];
  onSelect: (h: TrackHit) => void;
}) {
  const visible = hits.slice(0, visibleCount);
  const hiddenCount = Math.max(0, hits.length - visibleCount);
  const gridColumns = Math.max(MIN_INITIAL_PER_CAMERA, Math.min(rowColumns, visible.length || 1));

  return (
    <div className="bf-gradient-border">
      <div className="bf-gradient-border-inner space-y-2 p-2.5">
        <h3 className="text-center text-sm font-medium text-bf-text">
          Camera {cameraLabel}
          <span className="ml-1.5 text-xs font-normal text-bf-muted">({hits.length})</span>
        </h3>
        <DynamicResultsGrid columns={gridColumns}>
          {visible.map((h) => (
            <ResultHitTile
              key={h.track_key}
              hit={h}
              vllmPrompt={vllmPrompt}
              sort={sort}
              onSelect={onSelect}
              compact
            />
          ))}
        </DynamicResultsGrid>
        {(canShowMore || canShowLess) && (
          <div className="flex items-center justify-center gap-4 pt-0.5">
            {canShowLess && (
              <button
                type="button"
                onClick={onShowLess}
                className={SHOW_MORE_CLASS + " inline-flex items-center gap-1"}
              >
                <ChevronUp size={15} aria-hidden />
                Show less
              </button>
            )}
            {canShowMore && (
              <button
                type="button"
                onClick={onShowMore}
                className={SHOW_MORE_CLASS + " inline-flex items-center gap-1"}
              >
                Show more{hiddenCount > 0 ? ` (${hiddenCount})` : ""}
                <ChevronDown size={15} aria-hidden />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Search() {
  const [filters, setFilters] = useState<FilterState>(() => readUrlFilters());
  const [searchMode, setSearchMode] = useState<SearchMode>(readSearchMode);
  const [exclusiveByMode, setExclusiveByMode] = useState<ExclusiveByMode>(
    readExclusiveByMode,
  );
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [activeQuery, setActiveQuery] = useState(readInitialQuery);
  const [searchDraft, setSearchDraft] = useState(readInitialQuery);
  const placeholders = SEARCH_PLACEHOLDERS[searchMode];
  const searchPlaceholder = placeholders[placeholderIdx % placeholders.length];

  useEffect(() => {
    setPlaceholderIdx(0);
    if (placeholders.length < 2) return;
    const id = window.setInterval(
      () => setPlaceholderIdx((i) => (i + 1) % placeholders.length),
      PLACEHOLDER_ROTATE_MS,
    );
    return () => window.clearInterval(id);
  }, [placeholders]);
  const [selected, setSelected] = useState<TrackHit | null>(null);
  const dismissSelected = useBackdropDismiss(
    () => setSelected(null),
    selected != null,
  );
  const selectedReplay = useDirectReplay(selected?.camera_id);
  const [resultsView, setResultsView] = useState<"all" | "by_camera">("all");
  const [showFilters, setShowFilters] = useState(false);
  // Typed thresholds keep their draft text while being edited so "0." is not
  // rounded away mid-keystroke; the committed number lands on blur or Enter.
  const [minScoreText, setMinScoreText] = useState(() =>
    String(readUrlFilters().minScore),
  );
  const [fetchLimit, setFetchLimit] = useState(INITIAL_FETCH);
  const [displayLimitAll, setDisplayLimitAll] = useState(INITIAL_FETCH);
  const [cameraVisibleRows, setCameraVisibleRows] = useState<Record<string, number>>({});
  const [perCameraFetchLimit, setPerCameraFetchLimit] = useState(MIN_INITIAL_PER_CAMERA);
  const resultsGridRef = useRef<HTMLDivElement>(null);
  const columns = useResultsColumns(resultsGridRef, MIN_TILE_WIDTH, GRID_GAP_PX);

  // Search is scoped server-side to the current store or camera profile, so the
  // camera pills list exactly the cameras that can return results.
  const { scopeParams, scopeKey, scopeLabel, scope } = useScope();

  const cameras = useQuery<Camera[]>({
    queryKey: ["cameras", scopeKey],
    queryFn: () => api.cameras.list(scopeParams),
    staleTime: 60_000,
  });

  const visibleCameras = useMemo(() => cameras.data ?? [], [cameras.data]);

  const config = useQuery({
    queryKey: ["config"],
    queryFn: api.config.get,
    staleTime: 60_000,
  });

  const vllmPrompt = captionPromptFromConfig(config.data?.vllm);

  const { mutate: searchMutate, ...mut } = useMutation({
    mutationFn: (body: SearchBody) => api.search(body, scopeParams),
  });

  const buildSearchBody = useCallback(
    (
      f: FilterState,
      query: string,
      limit: number,
      balanceByCamera: boolean,
      mode: SearchMode,
      exclusive = false,
    ): SearchBody | null => {
    const trimmed = query.trim();
    if (!trimmed) return null;
    const body: SearchBody = {
      q: trimmed,
      limit,
      sort: f.sort,
      min_score: f.minScore,
      // A spatial region belongs to one camera, so balancing results across
      // cameras would fight it.
      balance_cameras: balanceByCamera && !f.spatial,
    };
    if (mode !== "both") body.search_mode = mode;
    if (exclusive && (mode === "appearance" || mode === "activity")) {
      body.exclusive = true;
    }
    if (f.cameras.length) body.camera_ids = f.cameras;
    if (f.spatial) body.spatial = f.spatial;
    const fromIso = partsToIso(
      {
        date: f.fromDate,
        hour: f.fromHour,
        minute: f.fromMinute,
        ampm: f.fromAmPm,
      },
      "start",
    );
    const toIsoStr = partsToIso(
      {
        date: f.toDate,
        hour: f.toHour,
        minute: f.toMinute,
        ampm: f.toAmPm,
      },
      "end",
    );
    if (fromIso) body.from_ts = fromIso;
    if (toIsoStr) body.to_ts = toIsoStr;
    return body;
  }, []);

  const resetDisplayPaging = useCallback(() => {
    setCameraVisibleRows({});
    setDisplayLimitAll(Math.max(columns * ALL_VIEW_ROW_BATCH, columns));
    setFetchLimit(INITIAL_FETCH);
    setPerCameraFetchLimit(Math.max(MIN_INITIAL_PER_CAMERA, columns));
  }, [columns]);

  const runSearch = useCallback(
    (
      f: FilterState,
      query: string,
      view: "all" | "by_camera" = resultsView,
      mode: SearchMode = searchMode,
      exclusive = exclusiveOn(mode, exclusiveByMode),
    ) => {
      const balanced = view === "by_camera";
      const limit = balanced
        ? Math.max(MIN_INITIAL_PER_CAMERA, columns)
        : INITIAL_FETCH;
      const body = buildSearchBody(f, query, limit, balanced, mode, exclusive);
      if (!body) return;
      writeUrlFilters(f, query, mode, exclusive);
      resetDisplayPaging();
      if (balanced) {
        setPerCameraFetchLimit(limit);
      }
      searchMutate(body);
    },
    [
      buildSearchBody,
      columns,
      exclusiveByMode,
      resetDisplayPaging,
      resultsView,
      searchMode,
      searchMutate,
    ],
  );

  // Re-run when server-side filters or view mode change.
  useEffect(() => {
    if (!activeQuery.trim()) return;
    runSearch(filters, activeQuery, resultsView);
  }, [
    filters.fromDate,
    filters.fromHour,
    filters.fromMinute,
    filters.fromAmPm,
    filters.toDate,
    filters.toHour,
    filters.toMinute,
    filters.toAmPm,
    filters.cameras.join(","),
    // Both thresholds are applied server-side, so a change is a new search
    // rather than a refinement of the hits already on screen.
    filters.minScore,
    // Re-run when the drawn area or its threshold changes: the server does the
    // overlap filtering, so this is not a client-side refinement.
    filters.spatial
      ? `${filters.spatial.camera_id}:${filters.spatial.bbox.join(",")}:${filters.spatial.min_overlap}`
      : "",
    activeQuery,
    resultsView,
    searchMode,
    exclusiveByMode.appearance,
    exclusiveByMode.activity,
    runSearch,
  ]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const q = searchDraft.trim();
    if (!q) return;
    // The filters have been chosen by the time a search is submitted; leaving
    // the panel open pushes the results a screen down.
    setShowFilters(false);
    setActiveQuery(q);
    runSearch(filters, q);
  }

  function commitMinScore(raw: string) {
    const n = Number(raw);
    const next = Number.isFinite(n)
      ? Math.min(1, Math.max(0, n))
      : DEFAULT_MIN_SCORE;
    setMinScoreText(String(next));
    setFilters((f) => (f.minScore === next ? f : { ...f, minScore: next }));
  }

  function toggleCamera(id: string) {
    setFilters((f) => ({
      ...f,
      cameras: f.cameras.includes(id)
        ? f.cameras.filter((c) => c !== id)
        : [...f.cameras, id],
    }));
  }

  function clearFilters() {
    const empty = emptyDateTimeParts();
    setFilters((f) => {
      const next: FilterState = {
        sort: f.sort,
        minScore: DEFAULT_MIN_SCORE,
        fromDate: empty.date,
        fromHour: empty.hour,
        fromMinute: empty.minute,
        fromAmPm: empty.ampm,
        toDate: empty.date,
        toHour: empty.hour,
        toMinute: empty.minute,
        toAmPm: empty.ampm,
        cameras: [],
        sex: "",
        age: "",
        skin: "",
        hair: "",
        spatial: null,
      };
      writeUrlFilters(next, activeQuery, searchMode, exclusiveOn(searchMode, exclusiveByMode));
      return next;
    });
    setMinScoreText(String(DEFAULT_MIN_SCORE));
  }

  function selectSearchMode(next: SearchMode) {
    setSearchMode(next);
    writeUrlFilters(filters, activeQuery, next, exclusiveOn(next, exclusiveByMode));
  }

  function toggleExclusive(next: "appearance" | "activity") {
    setExclusiveByMode((prev) => {
      const updated = { ...prev, [next]: !prev[next] };
      writeUrlFilters(filters, activeQuery, next, updated[next]);
      return updated;
    });
    setSearchMode(next);
  }

  // Closing the dialog unmounts the scrubber without it emitting, so the clip
  // has to be cleared here or the next track opens showing the last one's video.
  useEffect(() => {
    selectedReplay.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.track_key]);

  const [localHits, setHits] = useState<TrackHit[]>([]);

  useEffect(() => {
    if (mut.data) setHits(mut.data);
  }, [mut.data]);

  useEffect(() => {
    setDisplayLimitAll((prev) => Math.max(prev, columns * ALL_VIEW_ROW_BATCH));
  }, [columns]);

  const displayedHits = useMemo(() => {
    const fromParts: DateTimeParts = {
      date: filters.fromDate,
      hour: filters.fromHour,
      minute: filters.fromMinute,
      ampm: filters.fromAmPm,
    };
    const toParts: DateTimeParts = {
      date: filters.toDate,
      hour: filters.toHour,
      minute: filters.toMinute,
      ampm: filters.toAmPm,
    };
    let hits = localHits.filter(
      (h) =>
        matchesCaptionFilters(vllmPrompt, h.summary, {
          sex: filters.sex || undefined,
          age: filters.age || undefined,
          skin: filters.skin || undefined,
          hair: filters.hair || undefined,
        }) && matchesTimeOfDayWindow(h.last_seen_utc, fromParts, toParts),
    );
    if (filters.sort === "recency") {
      hits = [...hits].sort(
        (a, b) =>
          new Date(b.last_seen_utc).getTime() - new Date(a.last_seen_utc).getTime(),
      );
    }
    return hits;
  }, [
    localHits,
    filters.sort,
    filters.sex,
    filters.age,
    filters.skin,
    filters.hair,
    filters.fromDate,
    filters.fromHour,
    filters.fromMinute,
    filters.fromAmPm,
    filters.toDate,
    filters.toHour,
    filters.toMinute,
    filters.toAmPm,
    vllmPrompt,
  ]);

  const cameraNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cameras.data ?? []) {
      map.set(c.id, c.name?.trim() || c.id);
    }
    return map;
  }, [cameras.data]);

  const hitsByCamera = useMemo(() => {
    const groups = new Map<string, TrackHit[]>();
    for (const h of displayedHits) {
      const list = groups.get(h.camera_id) ?? [];
      list.push(h);
      groups.set(h.camera_id, list);
    }
    const sortHits = (hits: TrackHit[]) => {
      if (filters.sort === "recency") {
        hits.sort(
          (a, b) =>
            new Date(b.last_seen_utc).getTime() - new Date(a.last_seen_utc).getTime(),
        );
      } else {
        hits.sort((a, b) => matchingScorePct(b) - matchingScorePct(a));
      }
      return hits;
    };
    const entries = [...groups.entries()].map(([cameraId, hits]) => ({
      cameraId,
      hits: sortHits(hits),
    }));
    entries.sort((a, b) => {
      if (filters.sort === "recency") {
        const ta = Math.max(...a.hits.map((h) => new Date(h.last_seen_utc).getTime()));
        const tb = Math.max(...b.hits.map((h) => new Date(h.last_seen_utc).getTime()));
        return tb - ta;
      }
      const sa = Math.max(...a.hits.map((h) => matchingScorePct(h)));
      const sb = Math.max(...b.hits.map((h) => matchingScorePct(h)));
      return sb - sa;
    });
    return entries;
  }, [displayedHits, filters.sort]);

  const basePerCamera = Math.max(MIN_INITIAL_PER_CAMERA, columns);

  const visibleCountForCamera = useCallback(
    (cameraId: string) => {
      const rows = cameraVisibleRows[cameraId] ?? 1;
      return basePerCamera + Math.max(0, rows - 1) * columns;
    },
    [basePerCamera, cameraVisibleRows, columns],
  );

  const visibleAllHits = useMemo(
    () => displayedHits.slice(0, displayLimitAll),
    [displayedHits, displayLimitAll],
  );

  const fetchMoreFromServer = useCallback(
    (view: "all" | "by_camera", nextLimit: number) => {
      if (mut.isPending) return;
      const balanced = view === "by_camera";
      if (balanced && nextLimit > MAX_FETCH) return;
      if (!balanced && nextLimit > MAX_FETCH) return;
      const body = buildSearchBody(
        filters,
        activeQuery,
        nextLimit,
        balanced,
        searchMode,
        exclusiveOn(searchMode, exclusiveByMode),
      );
      if (!body) return;
      if (balanced) {
        setPerCameraFetchLimit(nextLimit);
      } else {
        setFetchLimit(nextLimit);
      }
      searchMutate(body);
    },
    [
      activeQuery,
      buildSearchBody,
      exclusiveByMode,
      filters,
      mut.isPending,
      searchMode,
      searchMutate,
    ],
  );

  const canFetchMoreAll =
    displayedHits.length >= fetchLimit && fetchLimit < MAX_FETCH;

  const canFetchMoreByCamera = perCameraFetchLimit < MAX_FETCH;

  const canLoadMoreAll = useMemo(() => {
    const hasHiddenLocal = displayLimitAll < displayedHits.length;
    return hasHiddenLocal || canFetchMoreAll;
  }, [canFetchMoreAll, displayLimitAll, displayedHits.length]);

  const loadMoreAll = useCallback(() => {
    if (mut.isPending) return;
    const nextLimit = displayLimitAll + columns * ALL_VIEW_ROW_BATCH;
    setDisplayLimitAll(nextLimit);
    if (nextLimit > displayedHits.length && canFetchMoreAll) {
      fetchMoreFromServer("all", Math.min(MAX_FETCH, fetchLimit + FETCH_STEP));
    }
  }, [
    canFetchMoreAll,
    columns,
    displayLimitAll,
    displayedHits.length,
    fetchLimit,
    fetchMoreFromServer,
    mut.isPending,
  ]);

  const loadMoreForCamera = useCallback(
    (cameraId: string, hits: TrackHit[]) => {
      if (mut.isPending) return;
      const currentRows = cameraVisibleRows[cameraId] ?? 1;
      const nextRows = currentRows + 1;
      const nextVisible = basePerCamera + (nextRows - 1) * columns;
      setCameraVisibleRows((prev) => ({ ...prev, [cameraId]: nextRows }));
      if (nextVisible > hits.length && nextVisible > perCameraFetchLimit && canFetchMoreByCamera) {
        fetchMoreFromServer("by_camera", Math.min(MAX_FETCH, nextVisible));
      }
    },
    [
      basePerCamera,
      cameraVisibleRows,
      canFetchMoreByCamera,
      columns,
      fetchMoreFromServer,
      mut.isPending,
      perCameraFetchLimit,
    ],
  );

  const showLessForCamera = useCallback((cameraId: string) => {
    setCameraVisibleRows((prev) => {
      const nextRows = Math.max(1, (prev[cameraId] ?? 1) - 1);
      return { ...prev, [cameraId]: nextRows };
    });
  }, []);

  const isLoadingMore = mut.isPending && localHits.length > 0;

  const deleteMutation = useMutation({
    mutationFn: (track_key: string) => api.tracks.delete(track_key),
    onSuccess: (_, track_key) => {
      setHits((current) => current.filter((h) => h.track_key !== track_key));
      setSelected(null);
    },
  });

  const hasFilters = useMemo(
    () =>
      Boolean(
        hasDateTimeParts({
          date: filters.fromDate,
          hour: filters.fromHour,
          minute: filters.fromMinute,
          ampm: filters.fromAmPm,
        }) ||
          hasDateTimeParts({
            date: filters.toDate,
            hour: filters.toHour,
            minute: filters.toMinute,
            ampm: filters.toAmPm,
          }) ||
          filters.cameras.length ||
          filters.sex ||
          filters.age ||
          filters.skin ||
          filters.hair,
      ),
    [filters],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI-Powered Search"
        description="Search your footage for people by their appearance or activity in natural language."
      />

      <form onSubmit={onSubmit} className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="bf-input-gradient relative min-w-[12rem] flex-1">
            <div className="bf-gradient-border-inner relative">
              <SearchIcon
                className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-bf-muted"
                size={18}
              />
              <input
                className="bf-input w-full border-0 bg-transparent pl-10 text-sm shadow-none focus:ring-0"
                type="search"
                placeholder={searchPlaceholder}
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
              />
            </div>
          </div>
          <SearchSplitButton
            pending={mut.isPending}
            mode={searchMode}
            exclusive={exclusiveByMode}
            onSelectMode={selectSearchMode}
            onToggleExclusive={toggleExclusive}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            className={
              "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition " +
              (showFilters
                ? "border-brand-purple/50 bg-brand-purple/10 text-white"
                : "border-white/10 text-bf-muted hover:bg-white/5 hover:text-bf-text")
            }
          >
            <SlidersHorizontal size={16} />
            Advanced Filters
            {hasFilters && (
              <span className="h-1.5 w-1.5 rounded-full bg-brand-fuchsia" aria-hidden />
            )}
            {showFilters ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>

          {/* Sort and the view toggle both decide how the results below are
              arranged, so they sit together rather than one of them hiding
              inside the filters that decide WHICH results there are. */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-bf-muted">Sort</span>
              <SearchableSelect
                className="w-36"
                ariaLabel="Sort results"
                value={filters.sort}
                searchThreshold={99}
                onChange={(v) => {
                  const sort = v as FilterState["sort"];
                  setFilters((f) => {
                    const next = { ...f, sort };
                    writeUrlFilters(
                      next,
                      activeQuery,
                      searchMode,
                      exclusiveOn(searchMode, exclusiveByMode),
                    );
                    return next;
                  });
                }}
                options={[
                  { value: "relevance", label: "Relevance", hint: "Best match first" },
                  { value: "recency", label: "Recency", hint: "Most recent first" },
                ]}
              />
            </div>

            {/* `p-1` + `py-1.5` matches the dropdown's height beside it, so
                the pair reads as one row of controls. */}
            <div className="inline-flex rounded-lg border border-white/10 bg-bf-surface/60 p-1 text-sm">
              <button
                type="button"
                onClick={() => setResultsView("all")}
                className={
                  "rounded-md px-3 py-1.5 transition " +
                  (resultsView === "all"
                    ? "bg-bf-violet text-white"
                    : "text-bf-muted hover:text-bf-text")
                }
              >
                All cameras
              </button>
              <button
                type="button"
                onClick={() => setResultsView("by_camera")}
                className={
                  "rounded-md px-3 py-1.5 transition " +
                  (resultsView === "by_camera"
                    ? "bg-bf-violet text-white"
                    : "text-bf-muted hover:text-bf-text")
                }
              >
                By camera
              </button>
            </div>
          </div>
        </div>

        {showFilters && (
        <div className="bf-gradient-border">
          <div className="bf-gradient-border-inner space-y-4 p-4">
          {/* Row 1: cameras */}
          <div>
            <div className={FILTER_LABEL}>Cameras</div>
            {visibleCameras.length === 0 && (
              <p className="text-center text-xs text-bf-muted">
                No cameras in {scopeLabel}.
              </p>
            )}
            <div className="flex flex-wrap justify-center gap-2">
              {visibleCameras.map((c) => {
                const on = filters.cameras.includes(c.id);
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => toggleCamera(c.id)}
                    className={
                      "rounded-full px-3 py-1 transition " +
                      (on
                        ? "bg-bf-violet text-sm font-medium text-white"
                        : "border border-white/10 text-xs text-bf-muted hover:text-bf-text")
                    }
                    aria-pressed={on}
                  >
                    {c.name || c.id}
                  </button>
                );
              })}
            </div>
          </div>

          <hr className="bf-gradient-divider" />

          {/* Row 2: from/to date + time (wraps when narrow) */}
          <div className="flex flex-wrap items-end justify-center gap-x-8 gap-y-3 pt-1">
            <DateTimeBoundRow
              label="From"
              date={filters.fromDate}
              hour={filters.fromHour}
              minute={filters.fromMinute}
              ampm={filters.fromAmPm}
              onDate={(fromDate) => setFilters((f) => ({ ...f, fromDate }))}
              onTime={(parts) =>
                setFilters((f) => ({
                  ...f,
                  fromHour: parts.hour,
                  fromMinute: parts.minute,
                  fromAmPm: parts.ampm,
                }))
              }
            />
            <DateTimeBoundRow
              label="To"
              date={filters.toDate}
              hour={filters.toHour}
              minute={filters.toMinute}
              ampm={filters.toAmPm}
              onDate={(toDate) => setFilters((f) => ({ ...f, toDate }))}
              onTime={(parts) =>
                setFilters((f) => ({
                  ...f,
                  toHour: parts.hour,
                  toMinute: parts.minute,
                  toAmPm: parts.ampm,
                }))
              }
            />
          </div>

          <hr className="bf-gradient-divider" />

          {/* Row 3: appearance filters + the relevance floor */}
          <div className="flex flex-wrap items-end justify-center gap-4 pt-1">
            <label className="flex flex-col items-center">
              <span className={FILTER_LABEL + " flex items-center justify-center gap-1.5"}>
                Relevance ≥
                <InfoHint>
                  Hides anything matching less well than this. It is the same
                  number as the score printed on each result, on a 0–1 scale:
                  0.5 keeps hits that are at least a 50% match. Leave it at 0
                  to see everything the search finds.
                </InfoHint>
              </span>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                aria-label="Minimum relevance"
                className={FILTER_CONTROL + " w-24 text-bf-text"}
                value={minScoreText}
                onChange={(e) => setMinScoreText(e.target.value)}
                onBlur={(e) => commitMinScore(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitMinScore((e.target as HTMLInputElement).value);
                  }
                }}
              />
            </label>
            <FilterSelect
              label="Sex"
              value={filters.sex}
              onChange={(sex) => setFilters((f) => ({ ...f, sex }))}
              options={anyOption(["male", "female", "none"])}
            />
            <FilterSelect
              label="Age"
              value={filters.age}
              onChange={(age) => setFilters((f) => ({ ...f, age }))}
              options={anyOption(AGES)}
            />
            <FilterSelect
              label="Skin"
              value={filters.skin}
              onChange={(skin) => setFilters((f) => ({ ...f, skin }))}
              options={anyOption(SKINS)}
            />
            <FilterSelect
              label="Hair"
              value={filters.hair}
              onChange={(hair) => setFilters((f) => ({ ...f, hair }))}
              options={anyOption(HAIRS)}
            />
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="ml-auto inline-flex items-center gap-1 text-xs text-bf-muted hover:text-bf-text"
              >
                <X size={14} /> Clear filters
              </button>
            )}
          </div>

          <hr className="bf-gradient-divider" />

          {/* Last, because it is the only filter that changes what the search
              MEANS rather than narrowing it — and the only one that needs a
              picture. */}
          <SpatialFilterPanel
            cameras={visibleCameras}
            value={filters.spatial}
            onChange={(spatial) =>
              setFilters((f) =>
                sameSpatial(f.spatial, spatial) ? f : { ...f, spatial },
              )
            }
          />
          </div>
        </div>
        )}
      </form>

      {mut.isError && (
        <div className="bf-card border-red-400/40">
          <div className="text-red-300 text-sm">Search failed.</div>
        </div>
      )}

      {!mut.isPending && mut.isSuccess && displayedHits.length === 0 && (
        <div className="bf-card text-sm text-bf-muted">
          No results. Try a broader query or relax the filters.
        </div>
      )}

      {displayedHits.length > 0 && (
        <div ref={resultsGridRef} className="w-full space-y-4">
          <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-bf-muted">
            {displayedHits.length} result{displayedHits.length === 1 ? "" : "s"}
            {canFetchMoreAll ? "+" : ""}
          </span>
        </div>

      {resultsView === "all" ? (
        <DynamicResultsGrid columns={columns}>
          {visibleAllHits.map((h) => (
            <ResultHitTile
              key={h.track_key}
              hit={h}
              vllmPrompt={vllmPrompt}
              sort={filters.sort}
              onSelect={setSelected}
            />
          ))}
        </DynamicResultsGrid>
      ) : (
        <div className="space-y-2">
          {hitsByCamera.map(({ cameraId, hits }) => {
            const visibleCount = visibleCountForCamera(cameraId);
            const hiddenCount = Math.max(0, hits.length - visibleCount);
            const rows = cameraVisibleRows[cameraId] ?? 1;
            const rowCanShowMore =
              hiddenCount > 0 ||
              (canFetchMoreByCamera && hits.length >= perCameraFetchLimit);
            return (
            <CameraResultsRow
              key={cameraId}
              cameraLabel={cameraNameById.get(cameraId) ?? cameraId}
              hits={hits}
              visibleCount={visibleCount}
              rowColumns={columns}
              canShowMore={rowCanShowMore}
              canShowLess={rows > 1}
              onShowMore={() => loadMoreForCamera(cameraId, hits)}
              onShowLess={() => showLessForCamera(cameraId)}
              vllmPrompt={vllmPrompt}
              sort={filters.sort}
              onSelect={setSelected}
            />
            );
          })}
        </div>
      )}

          {resultsView === "all" && (
            <div className="flex flex-col items-center gap-1 py-2">
              {isLoadingMore ? (
                <span className="text-sm text-bf-muted">Loading more…</span>
              ) : canLoadMoreAll ? (
                <button type="button" onClick={loadMoreAll} className={SHOW_MORE_CLASS}>
                  Load more
                </button>
              ) : visibleAllHits.length > 0 ? (
                <span className="text-xs text-bf-muted">No more results</span>
              ) : null}
            </div>
          )}
        </div>
      )}

      {selected && (
        <div
          role="dialog"
          className="fixed inset-0 z-40 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
          {...dismissSelected}
        >
          <div
            className="bf-gradient-border h-[92vh] w-[96vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bf-gradient-border-inner flex h-full w-full flex-col gap-6 overflow-hidden p-6 md:flex-row">
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              {/* The clip TAKES OVER the picture area rather than appearing
                  below it: the pane is a fixed 92vh, so a second video box
                  would halve the frame the operator came here to look at. */}
              <div className="relative flex min-h-0 flex-1 flex-col">
                <ClipPlayerCanvas
                  canvasRef={selectedReplay.canvasRef}
                  visible={selectedReplay.playing && !selectedReplay.pending}
                  cameraId={selected?.camera_id}
                />
                {selectedReplay.playing ? (
                  <div className="absolute left-3 top-3 z-10 flex items-center gap-2 text-xs text-bf-muted">
                    <span>
                      {selectedReplay.startedAt
                        ? new Date(selectedReplay.startedAt).toLocaleString()
                        : "Recorded video"}
                    </span>
                    <button
                      type="button"
                      onClick={() => selectedReplay.stop()}
                      className="inline-flex items-center gap-1 hover:text-bf-text"
                    >
                      <X size={13} /> Back to the still frame
                    </button>
                  </div>
                ) : (
                  <ZoomableTrackFrame
                    src={selected.frame_url}
                    alt={selected.track_key}
                    bbox={selected.bbox}
                  />
                )}
                {selectedReplay.pending && (
                  <div className="absolute inset-0 grid place-items-center">
                    <Loader2 size={28} className="animate-spin text-white/80" />
                  </div>
                )}
              </div>
              {/* Directly under the picture: the clip is about what is on
                  screen, so the control that widens it belongs there rather
                  than at the bottom of a scrolling details column. */}
              <ClipScrubber
                cameraId={selected.camera_id}
                cameraName={cameraNameById.get(selected.camera_id)}
                trackKey={selected.track_key}
                startMs={new Date(selected.last_seen_utc).getTime()}
                endMs={new Date(selected.last_seen_utc).getTime()}
                snapshotUrl={selected.frame_url}
                replay={selectedReplay}
              />
            </div>
            <div className="md:w-96 flex flex-col h-full overflow-hidden">
              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                <TrackDetailPanel track={selected} prompt={vllmPrompt} />
              </div>
              <div className="mt-6 pt-4 border-t border-white/10 flex justify-between gap-4">
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="bf-button border border-white/10 bg-transparent hover:bg-white/5 flex-1"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Delete this track record and its associated frame from storage?",
                      )
                    ) {
                      deleteMutation.mutate(selected.track_key);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="rounded-md px-4 py-2 text-sm font-medium transition flex items-center justify-center gap-2 bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/30 flex-1"
                >
                  <Trash2 size={16} />
                  {deleteMutation.isPending ? "Deleting..." : "Delete Track"}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
